import type { ReactElement } from 'react'
import { useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { useLocation, useNavigate } from 'react-router'

import { AuthLayout } from './AuthLayout'

/**
 * OAuth callback handler page.
 *
 * The backend OAuth callback endpoint (/api/v1/unauthenticated/auth/oauth/callback)
 * handles the actual token exchange and sets the session cookie, then redirects here.
 *
 * This page:
 * 1. Extracts the redirect path from the URL state parameter
 * 2. Redirects the user to their intended destination (or home)
 *
 * If there's an error, the backend redirects to /auth/login with an oauthError param.
 */
export const OAuthCallback = (): ReactElement => {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const location = useLocation()

  useEffect(() => {
    // Extract redirect path from state parameter (set during OAuth initiation)
    const params = new URLSearchParams(location.search)
    const redirectPath = params.get('state') ?? '/'

    // Navigate to the intended destination
    void navigate(redirectPath, { replace: true })
  }, [location.search, navigate])

  // Show loading state while redirecting
  return (
    <AuthLayout subtitle={t('auth.completing_sign_in')} title={t('auth.please_wait')} type="login">
      <div className="auth-loading">{t('auth.redirecting')}</div>
    </AuthLayout>
  )
}
